import { DiffEditor } from "@monaco-editor/react";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, Modal, Spinner, errText, fmtBytes, fmtDate } from "../../components/ui";
import { languageFor } from "../../editor/CodeEditor";
import {
  exportUrl,
  useCreateVersion,
  useDeleteVersion,
  useImportProjectPath,
  useImportProjectZip,
  useRestoreVersion,
  useVersionDiff,
  useVersions,
  versionFileUrl,
  type FileChange,
  type VersionInfo,
} from "./versionsApi";
import "./versions.css";

type Side = "working" | "parent" | string;

export function VersionsPage() {
  const session = useSession();
  const { projectId, setProject } = session;
  const list = useVersions(projectId);
  const create = useCreateVersion(projectId);
  const restore = useRestoreVersion(projectId);
  const del = useDeleteVersion(projectId);

  const versions = list.data?.versions ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [against, setAgainst] = useState<Side>("working");
  const [file, setFile] = useState<string | null>(null);
  const [snapshotting, setSnapshotting] = useState(false);
  const [restoring, setRestoring] = useState<{ version: VersionInfo; paths?: string[] } | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!versions.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !versions.some((v) => v.id === selectedId)) setSelectedId(versions[0].id);
  }, [versions, selectedId]);

  const selected = versions.find((v) => v.id === selectedId) ?? null;
  const againstId = against === "parent" ? selected?.parent ?? "" : against;
  // The diff API reports changes going from `version` to `against`; for a parent comparison we
  // want "what this version changed", so query parent -> version instead.
  const diffFrom = against === "parent" ? selected?.parent ?? null : selectedId;
  const diffTo = against === "parent" ? selectedId ?? "" : againstId;
  const diff = useVersionDiff(projectId, diffFrom, diffTo);
  const changes = diff.data?.changes ?? [];

  useEffect(() => {
    if (file && !changes.some((c) => c.path === file)) setFile(null);
  }, [changes, file]);

  useEffect(() => {
    if (against === "parent" && selected && !selected.parent) setAgainst("working");
  }, [against, selected]);

  const doSnapshot = async (message: string, tags: string[]) => {
    if (!projectId) return;
    try {
      const v = await create.mutateAsync({ message, tags });
      setSnapshotting(false);
      if (v.created) {
        session.toast(`Saved ${v.id}`);
        setSelectedId(v.id);
      } else session.toast(`Nothing changed since ${v.id}`, "info");
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  const doRestore = async () => {
    if (!restoring) return;
    try {
      const r = await restore.mutateAsync({ versionId: restoring.version.id, paths: restoring.paths });
      session.toast(
        `Restored ${r.version}: ${r.written.length} written, ${r.removed.length} removed` +
          (r.checkpoint ? ` (checkpoint ${r.checkpoint})` : ""),
      );
      setRestoring(null);
      setAgainst("working");
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  const doDelete = async (v: VersionInfo) => {
    if (!confirm(`Delete ${v.id} "${v.message}"? Files shared with other versions are kept.`)) return;
    try {
      await del.mutateAsync(v.id);
      session.toast(`Deleted ${v.id}`);
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  if (!projectId) return <Empty title="No project selected" />;

  const dirty = list.data?.dirty;
  const latest = list.data?.latest;

  return (
    <div className="page versions-page">
      <div className="page-head">
        <h1>Versions</h1>
        <span className="sub">
          {versions.length} snapshot{versions.length === 1 ? "" : "s"}
          {latest ? ` · latest ${latest}` : ""}
        </span>
        <span className="spacer" />
        <button className="btn ghost sm" onClick={() => setImporting(true)}>
          Import…
        </button>
        <a className="btn ghost sm" href={exportUrl(projectId)} download title="Download the working tree as a zip">
          Export zip
        </a>
        <button className="btn primary sm" onClick={() => setSnapshotting(true)} disabled={create.isPending}>
          + Snapshot
        </button>
      </div>

      {dirty && dirty.changes > 0 ? (
        <div className="banner warn">
          <strong>{dirty.changes} uncommitted change{dirty.changes === 1 ? "" : "s"}</strong> since {latest}
          <span className="dim">
            {" "}
            (+{dirty.added} −{dirty.removed} ~{dirty.modified})
          </span>
          <span className="spacer" />
          <button
            className="btn sm"
            onClick={() => {
              if (latest) {
                setSelectedId(latest);
                setAgainst("working");
              }
            }}
          >
            Review
          </button>
          <button className="btn primary sm" onClick={() => setSnapshotting(true)}>
            Snapshot now
          </button>
        </div>
      ) : versions.length && dirty ? (
        <div className="banner ok">
          Working tree matches <strong>{latest}</strong>.
        </div>
      ) : null}

      {list.isLoading ? (
        <Spinner label="Loading versions…" />
      ) : !versions.length ? (
        <Empty title="No versions yet">
          Snapshots are content-addressed copies of the whole project (triggers, reports, inputs, frame, tests). Take one before risky edits and
          restore any file later.
          <div style={{ marginTop: 10 }}>
            <button className="btn primary" onClick={() => setSnapshotting(true)}>
              Take first snapshot
            </button>
          </div>
        </Empty>
      ) : (
        <Split id="versions" className="versions-body fill" sizes={[320, "1fr"]} minSizes={[200, 400]}>
          <aside className="timeline">
            {versions.map((v) => (
              <div
                key={v.id}
                className={`tl-item${v.id === selectedId ? " on" : ""}${v.auto ? " auto" : ""}`}
                onClick={() => {
                  setSelectedId(v.id);
                  setFile(null);
                }}
              >
                <div className="tl-dot" />
                <div className="tl-main">
                  <div className="tl-head">
                    <span className="mono">{v.id}</span>
                    {v.id === latest ? <span className="badge">latest</span> : null}
                    {v.auto ? <span className="badge warn">auto</span> : null}
                    {v.tags.map((t) => (
                      <span key={t} className="badge tag">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="tl-msg">{v.message || <span className="faint">(no message)</span>}</div>
                  <div className="tl-meta dim">
                    {fmtDate(v.created_at)}
                    {v.user ? ` · ${v.user}` : ""} · {v.file_count} files · {fmtBytes(v.size)}
                    {stats(v.stats) ? <span className="mono"> · {stats(v.stats)}</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </aside>

          <section className="detail">
            {selected ? (
              <>
                <div className="toolbar">
                  <span className="mono">{selected.id}</span>
                  <span className="dim">→</span>
                  <select className="inp sm" value={against} onChange={(e) => (setAgainst(e.target.value), setFile(null))}>
                    <option value="working">working tree</option>
                    {selected.parent ? <option value="parent">its parent ({selected.parent})</option> : null}
                    {versions
                      .filter((v) => v.id !== selected.id)
                      .map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.id} — {v.message.slice(0, 40)}
                        </option>
                      ))}
                  </select>
                  {diff.data ? (
                    <span className="dim small">
                      +{diff.data.summary.added} −{diff.data.summary.removed} ~{diff.data.summary.modified}
                    </span>
                  ) : null}
                  <span className="spacer" />
                  <a className="btn ghost sm" href={exportUrl(projectId, selected.id)} download title="Download this version as a zip">
                    Export {selected.id}
                  </a>
                  <button className="btn sm" onClick={() => setRestoring({ version: selected })} disabled={selected.id === latest && !(dirty && dirty.changes)}>
                    Restore all…
                  </button>
                  <button className="btn ghost sm danger" onClick={() => doDelete(selected)} disabled={del.isPending}>
                    Delete
                  </button>
                </div>

                <Split id="versions-diff" className="diff-area fill" sizes={[300, "1fr"]} minSizes={[160, 300]}>
                  <div className="file-list">
                    {diff.isLoading ? (
                      <Spinner label="Diffing…" />
                    ) : diff.error ? (
                      <div className="sev-error small">{errText(diff.error)}</div>
                    ) : !changes.length ? (
                      <div className="dim small" style={{ padding: 8 }}>
                        No differences.
                      </div>
                    ) : (
                      changes.map((c) => (
                        <div key={c.path} className={`fl-item ${c.status}${file === c.path ? " on" : ""}`} onClick={() => setFile(c.path)}>
                          <span className={`st st-${c.status}`}>{c.status[0].toUpperCase()}</span>
                          <span className="mono path" title={c.path}>
                            {c.path}
                          </span>
                          <span className="dim small">{sizeDelta(c)}</span>
                          {against === "working" && c.status !== "added" ? (
                            <button
                              className="btn ghost xs"
                              title={`Restore ${c.path} from ${selected.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setRestoring({ version: selected, paths: [c.path] });
                              }}
                            >
                              ↺
                            </button>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="diff-view">
                    {file && projectId ? (
                      <FileDiff
                        projectId={projectId}
                        path={file}
                        change={changes.find((c) => c.path === file)!}
                        oldRef={diffFrom}
                        newRef={diffTo || "working"}
                      />
                    ) : (
                      <div className="empty small">
                        <span className="dim">Select a file to see a side-by-side diff.</span>
                      </div>
                    )}
                  </div>
                </Split>
              </>
            ) : null}
          </section>
        </Split>
      )}

      {snapshotting ? <SnapshotDialog onClose={() => setSnapshotting(false)} onSave={doSnapshot} busy={create.isPending} dirty={dirty ?? null} /> : null}
      {restoring ? (
        <Modal
          title={restoring.paths ? `Restore ${restoring.paths.length} file(s) from ${restoring.version.id}` : `Restore ${restoring.version.id}`}
          onClose={() => setRestoring(null)}
          footer={
            <>
              <button className="btn" onClick={() => setRestoring(null)}>
                Cancel
              </button>
              <button className="btn primary" onClick={doRestore} disabled={restore.isPending}>
                {restore.isPending ? "Restoring…" : "Restore"}
              </button>
            </>
          }
        >
          {restoring.paths ? (
            <>
              <p>The following file(s) in the working tree will be overwritten with the contents from {restoring.version.id}:</p>
              <ul className="mono small">
                {restoring.paths.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </>
          ) : (
            <p>
              The whole working tree will be replaced by <strong>{restoring.version.id}</strong> — &ldquo;{restoring.version.message}&rdquo;. Files that
              did not exist in that version are removed.
            </p>
          )}
          {dirty && dirty.changes ? (
            <p className="dim small">
              You have {dirty.changes} uncommitted change(s); an automatic checkpoint snapshot is taken first so nothing is lost.
            </p>
          ) : null}
        </Modal>
      ) : null}
      {importing ? (
        <ImportDialog
          onClose={() => setImporting(false)}
          onImported={(id) => {
            setImporting(false);
            setProject(id);
          }}
        />
      ) : null}
    </div>
  );
}

function stats(s: VersionInfo["stats"]): string {
  const parts: string[] = [];
  if (s.added) parts.push(`+${s.added}`);
  if (s.removed) parts.push(`−${s.removed}`);
  if (s.modified) parts.push(`~${s.modified}`);
  return parts.join(" ");
}

function sizeDelta(c: FileChange): string {
  if (c.status === "added") return fmtBytes(c.new_size);
  if (c.status === "removed") return `was ${fmtBytes(c.old_size)}`;
  const d = (c.new_size ?? 0) - (c.old_size ?? 0);
  return d === 0 ? fmtBytes(c.new_size) : `${d > 0 ? "+" : ""}${d} B`;
}

async function fetchText(url: string): Promise<string> {
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 404) return "";
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.text();
}

function useFileContent(projectId: string, ref: string | null, path: string, exists: boolean) {
  const [state, setState] = useState<{ text: string; loading: boolean; error: string | null }>({ text: "", loading: true, error: null });
  useEffect(() => {
    let live = true;
    if (!exists || !ref) {
      setState({ text: "", loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    const p =
      ref === "working"
        ? api.get<{ content: string }>(`/projects/${projectId}/files/${path}`).then((b) => b.content)
        : fetchText(versionFileUrl(projectId, ref, path));
    p.then(
      (text) => live && setState({ text, loading: false, error: null }),
      (e) => live && setState({ text: "", loading: false, error: errText(e) }),
    );
    return () => {
      live = false;
    };
  }, [projectId, ref, path, exists]);
  return state;
}

function FileDiff({
  projectId,
  path,
  change,
  oldRef,
  newRef,
}: {
  projectId: string;
  path: string;
  change: FileChange;
  oldRef: string | null;
  newRef: string;
}) {
  const theme = useSession((s) => s.theme);
  const [mode, setMode] = useState<"side" | "unified">("side");
  const oldSide = useFileContent(projectId, oldRef, path, change.status !== "added");
  const newSide = useFileContent(projectId, newRef, path, change.status !== "removed");
  const language = useMemo(() => languageFor(path), [path]);

  return (
    <div className="file-diff">
      <div className="toolbar small">
        <span className="mono">{path}</span>
        <span className={`badge ${change.status === "added" ? "ok" : change.status === "removed" ? "err" : ""}`}>{change.status}</span>
        {change.binary ? <span className="badge warn">binary</span> : null}
        <span className="spacer" />
        <div className="seg">
          <button className={mode === "side" ? "on" : ""} onClick={() => setMode("side")}>
            Side by side
          </button>
          <button className={mode === "unified" ? "on" : ""} onClick={() => setMode("unified")}>
            Unified
          </button>
        </div>
      </div>
      {change.binary ? (
        <div className="empty small">
          <span className="dim">
            Binary file ({fmtBytes(change.old_size)} → {fmtBytes(change.new_size)}).
          </span>
        </div>
      ) : mode === "unified" ? (
        <pre className="diff full">
          {change.diff ? (
            change.diff.split("\n").map((l, i) => (
              <div key={i} className={l.startsWith("+") && !l.startsWith("+++") ? "add" : l.startsWith("-") && !l.startsWith("---") ? "del" : l.startsWith("@@") ? "hunk" : ""}>
                {l}
              </div>
            ))
          ) : (
            <span className="dim">No textual diff available.</span>
          )}
          {change.truncated ? <div className="dim">… diff truncated</div> : null}
        </pre>
      ) : oldSide.loading || newSide.loading ? (
        <Spinner label="Loading file contents…" />
      ) : oldSide.error || newSide.error ? (
        <div className="sev-error small" style={{ padding: 8 }}>
          {oldSide.error ?? newSide.error}
        </div>
      ) : (
        <div className="diff-editor">
          <DiffEditor
            original={oldSide.text}
            modified={newSide.text}
            language={language}
            theme={theme === "dark" ? "rs-dark" : "rs-light"}
            options={{
              readOnly: true,
              originalEditable: false,
              renderSideBySide: true,
              minimap: { enabled: false },
              fontSize: 12.5,
              fontFamily: '"Cascadia Code", "JetBrains Mono", Consolas, monospace',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              ignoreTrimWhitespace: false,
            }}
          />
        </div>
      )}
    </div>
  );
}

function SnapshotDialog({
  onClose,
  onSave,
  busy,
  dirty,
}: {
  onClose: () => void;
  onSave: (message: string, tags: string[]) => void;
  busy: boolean;
  dirty: { changes: number; paths: string[] } | null;
}) {
  const [message, setMessage] = useState("");
  const [tags, setTags] = useState("");
  const submit = () => {
    if (!message.trim()) return;
    onSave(
      message.trim(),
      tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    );
  };
  return (
    <Modal
      title="Snapshot project"
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy || !message.trim()}>
            {busy ? "Saving…" : "Save snapshot"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label>Message</label>
        <input
          className="inp"
          autoFocus
          value={message}
          placeholder="e.g. tighten cruise tolerance to 10 ft"
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <label>Tags</label>
        <input className="inp" value={tags} placeholder="comma separated, e.g. release, v2.1" onChange={(e) => setTags(e.target.value)} />
      </div>
      {dirty ? (
        <div className="dim small" style={{ marginTop: 10 }}>
          {dirty.changes ? (
            <>
              {dirty.changes} changed file(s):
              <ul className="mono small compact">
                {dirty.paths.slice(0, 12).map((p) => (
                  <li key={p}>{p}</li>
                ))}
                {dirty.paths.length > 12 ? <li>… {dirty.paths.length - 12} more</li> : null}
              </ul>
            </>
          ) : (
            "No changes since the latest version — the snapshot will be skipped unless the content differs."
          )}
        </div>
      ) : null}
    </Modal>
  );
}

function ImportDialog({ onClose, onImported }: { onClose: () => void; onImported: (id: string) => void }) {
  const session = useSession();
  const importZip = useImportProjectZip();
  const importPath = useImportProjectPath();
  const [tab, setTab] = useState<"zip" | "path">("zip");
  const [file, setFile] = useState<File | null>(null);
  const [path, setPath] = useState("");
  const [name, setName] = useState("");
  const busy = importZip.isPending || importPath.isPending;

  const submit = async () => {
    try {
      const r =
        tab === "zip"
          ? await importZip.mutateAsync({ file: file!, name: name.trim() || undefined })
          : await importPath.mutateAsync({ path: path.trim(), name: name.trim() || undefined });
      session.toast(`Imported "${r.name}"`);
      onImported(r.id);
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  return (
    <Modal
      title="Import project"
      onClose={onClose}
      width={480}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy || (tab === "zip" ? !file : !path.trim())}>
            {busy ? "Importing…" : "Import"}
          </button>
        </>
      }
    >
      <div className="seg" style={{ marginBottom: 10 }}>
        <button className={tab === "zip" ? "on" : ""} onClick={() => setTab("zip")}>
          Zip archive
        </button>
        <button className={tab === "path" ? "on" : ""} onClick={() => setTab("path")}>
          Server folder
        </button>
      </div>
      <div className="form-grid">
        {tab === "zip" ? (
          <>
            <label>Archive</label>
            <input className="inp" type="file" accept=".zip,application/zip" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </>
        ) : (
          <>
            <label>Folder path</label>
            <input className="inp" value={path} placeholder="/data/projects/my_project (as seen by the API server)" onChange={(e) => setPath(e.target.value)} />
          </>
        )}
        <label>Name</label>
        <input className="inp" value={name} placeholder="optional — defaults to the project's own name" onChange={(e) => setName(e.target.value)} />
      </div>
      <p className="dim small" style={{ marginTop: 10 }}>
        Exported zips from Reports Studio (or any folder in the git-friendly on-disk layout) can be imported. Caches and version history are not
        carried over.
      </p>
    </Modal>
  );
}
